import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BlockHead,
  BlockTitle,
  Button,
  Icon,
  BlockDes,
  BlockHeadContent,
  Block,
  BlockBetween,
} from "../../../components/Component";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import LogoDark from "../../../images/logo-dark.png";
import { Link } from "react-router-dom";
import { Alert, Spinner } from "reactstrap";
import { API_BASE_URL, getPublicUrl } from "../../../utils/Utils";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { invoiceDisplayId, formatMoneyAmount, patientDisplayName } from "./invoiceHelpers";
import swal from "sweetalert";

const InvoiceDetails = ({ match }) => {
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    const id = match.params.id;
    if (!id || !token) {
      setLoading(false);
      setError(!token ? "Not signed in" : "Invalid invoice");
      return undefined;
    }
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    axios
      .get(`${API_BASE_URL}/api/v1/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: ac.signal,
      })
      .then((res) => setInv(res.data.data || null))
      .catch((err) => {
        if (!isAbortError(err)) {
          setInv(null);
          setError(err.response?.data?.message || err.message || "Invoice not found");
        }
      })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [match.params.id, token]);

  const lines = inv && Array.isArray(inv.lines) ? inv.lines : [];
  const lineSum = lines.reduce((acc, line) => acc + (Number(line.lineTotal) || 0), 0);
  const createdLabel = inv?.createdAt ? new Date(inv.createdAt).toLocaleString("en-GB") : "—";
  const partnerName = inv?.medicalPartner?.organization_name || "—";
  const billing = inv?.medicalPartner?.billingProfile;
  const addressLine =
    billing &&
    [billing.billingAddressLine1, billing.billingCity, billing.billingPostalCode, billing.billingCountry]
      .filter(Boolean)
      .join(", ");

  const downloadInvoicePdf = () => {
    if (!inv?._id || !token) return;
    setPdfLoading(true);
    axios
      .get(`${API_BASE_URL}/api/v1/invoices/${inv._id}/pdf`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${invoiceDisplayId(inv).replace(/[^a-zA-Z0-9._-]/g, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(async (err) => {
        let msg = "Could not download PDF";
        const d = err.response?.data;
        if (d instanceof Blob) {
          try {
            const t = await d.text();
            if (t) msg = t.slice(0, 400);
          } catch {
            /* ignore */
          }
        } else if (d && typeof d === "object" && d.message) {
          msg = String(d.message);
        } else if (err.message) {
          msg = err.message;
        }
        swal("Error", msg, "error");
      })
      .finally(() => setPdfLoading(false));
  };

  return (
    <React.Fragment>
      <Head title="Invoice Detail"></Head>
      <Content>
        {loading && (
          <div className="p-5 text-center">
            <Spinner color="primary" />
          </div>
        )}
        {!loading && error && (
          <Alert color="danger" className="mt-3">
            {error}
          </Alert>
        )}
        {!loading && inv && (
          <>
            <BlockHead>
              <BlockBetween className="g-3">
                <BlockHeadContent>
                  <BlockTitle>
                    Invoice <strong className="text-primary small">#{invoiceDisplayId(inv)}</strong>
                  </BlockTitle>
                  <BlockDes className="text-soft">
                    <ul className="list-inline">
                      <li>
                        Created: <span className="text-base">{createdLabel}</span>
                      </li>
                      <li className="text-capitalize">Status: {inv.status}</li>
                      <li className="text-capitalize">Kind: {inv.kind}</li>
                    </ul>
                  </BlockDes>
                </BlockHeadContent>
                <BlockHeadContent>
                  <Link to={getPublicUrl("invoice-list")}>
                    <Button color="light" outline className="bg-white d-none d-sm-inline-flex">
                      <Icon name="arrow-left"></Icon>
                      <span>Back</span>
                    </Button>
                  </Link>
                  <Link to={getPublicUrl("invoice-list")}>
                    <Button color="light" outline className="btn-icon bg-white d-inline-flex d-sm-none">
                      <Icon name="arrow-left"></Icon>
                    </Button>
                  </Link>
                </BlockHeadContent>
              </BlockBetween>
            </BlockHead>

            {(inv.status === "draft" || inv.status === "pending_issue") && !inv.invoiceNumber ? (
              <Alert color="info" className="mb-3">
                A legal invoice number is assigned when you transition this document to <strong>issued</strong> (automatic sequence
                per year). You can still download a <strong>draft</strong> PDF (watermarked) for internal review.
              </Alert>
            ) : null}
            {inv.invoiceNumber && ["issued", "paid", "void", "credited"].includes(inv.status) ? (
              <Alert color="light" className="mb-3 border">
                Legal reference <strong>{inv.invoiceNumber}</strong>. Download the PDF for archiving. Issuer details and footer text
                are configured in{" "}
                <Link to={{ pathname: getPublicUrl("admin-settings"), search: "?tab=billing" }}>Settings → Billing & invoices</Link>.
              </Alert>
            ) : null}

            <Block>
              <div className="invoice">
                <div className="invoice-action d-flex flex-wrap align-items-center">
                  <Button
                    size="lg"
                    color="primary"
                    className="btn-dim me-2 mb-2"
                    type="button"
                    disabled={pdfLoading}
                    onClick={downloadInvoicePdf}
                  >
                    <Icon name="file-pdf"></Icon>
                    <span className="ms-1">{pdfLoading ? "…" : "Download PDF"}</span>
                  </Button>
                  <Link
                    className="mb-2"
                    to={getPublicUrl(`invoice-print/${inv._id}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button size="lg" color="primary" outline className="btn-icon btn-white btn-dim">
                      <Icon name="printer-fill"></Icon>
                    </Button>
                  </Link>
                </div>
                <div className="invoice-wrap">
                  <div className="invoice-brand text-center">
                    <img src={LogoDark} alt="" />
                  </div>

                  <div className="invoice-head">
                    <div className="invoice-contact">
                      <span className="overline-title">Bill to</span>
                      <div className="invoice-contact-info">
                        <h4 className="title">{patientDisplayName(inv.patient)}</h4>
                        <ul className="list-plain">
                          {inv.patient && inv.patient.email && (
                            <li>
                              <Icon name="mail"></Icon>
                              <span>{inv.patient.email}</span>
                            </li>
                          )}
                          {partnerName !== "—" && (
                            <li>
                              <Icon name="building"></Icon>
                              <span>Partner: {partnerName}</span>
                            </li>
                          )}
                          {addressLine && (
                            <li>
                              <Icon name="map-pin-fill"></Icon>
                              <span>{addressLine}</span>
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                    <div className="invoice-desc">
                      <h3 className="title">Invoice</h3>
                      <ul className="list-plain">
                        <li className="invoice-id">
                          <span>Reference</span>:<span>{invoiceDisplayId(inv)}</span>
                        </li>
                        <li className="invoice-date">
                          <span>Date</span>:<span>{createdLabel}</span>
                        </li>
                        <li>
                          <span>Currency</span>:<span>{inv.currency || "EUR"}</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="invoice-bills">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th className="w-60">Description</th>
                            <th>Unit</th>
                            <th>Qty</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lines.length > 0 ? (
                            lines.map((line) => (
                              <tr key={line._id || `${line.description}-${line.lineTotal}`}>
                                <td>{line.description}</td>
                                <td>{formatMoneyAmount(line.unitAmount, inv.currency)}</td>
                                <td>{line.quantity}</td>
                                <td>{formatMoneyAmount(line.lineTotal, inv.currency)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-soft">
                                No line items — total shown from document total.
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot>
                          {inv.subtotalExclTax != null && (
                            <tr>
                              <td colSpan="3">Subtotal (excl. tax)</td>
                              <td>{formatMoneyAmount(inv.subtotalExclTax, inv.currency)}</td>
                            </tr>
                          )}
                          {inv.taxAmount != null && (
                            <tr>
                              <td colSpan="3">Tax</td>
                              <td>{formatMoneyAmount(inv.taxAmount, inv.currency)}</td>
                            </tr>
                          )}
                          {inv.kind === "teleconsultation_settlement" && (
                            <>
                              <tr>
                                <td colSpan="3">Platform commission</td>
                                <td>{formatMoneyAmount(inv.platformCommissionAmount, inv.currency)}</td>
                              </tr>
                              {inv.partnerNetAmount != null && (
                                <tr>
                                  <td colSpan="3">Partner net</td>
                                  <td>{formatMoneyAmount(inv.partnerNetAmount, inv.currency)}</td>
                                </tr>
                              )}
                            </>
                          )}
                          <tr>
                            <td colSpan="3">Total</td>
                            <td>{formatMoneyAmount(inv.totalAmount, inv.currency)}</td>
                          </tr>
                          {lines.length > 0 && (
                            <tr className="text-soft small">
                              <td colSpan="4">Sum of lines: {formatMoneyAmount(lineSum, inv.currency)}</td>
                            </tr>
                          )}
                        </tfoot>
                      </table>
                      <div className="nk-notes ff-italic fs-12px text-soft">
                        Use <strong>Download PDF</strong> for the generated invoice file. Browser print is an informal preview only.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Block>
          </>
        )}
      </Content>
    </React.Fragment>
  );
};
export default InvoiceDetails;
